import lottieDefaults from '@/assets/animations/lottie-defaults';
import qrCodeHeader from '@/assets/animations/qr-code-header.json';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Profile, SubscriptionStatus } from '@cinemark/profile-api-client';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import React from 'react';
import qrCodeModalStyles from './qr-code-modal.module.scss';
const Lottie = dynamic(() => import('react-lottie'), { ssr: false });

type QRCodeModalProps = {
  children: React.ReactNode;
  profile: Profile;
  qrCode: string;
};

export const QRCodeModal = ({ children, profile, qrCode }: QRCodeModalProps): React.JSX.Element => {
  const lottieOptions = { ...lottieDefaults, animationData: qrCodeHeader };

  const displaySubscriptionStateText = (): React.ReactNode => {
    const content: React.ReactNode[] = [];

    if (profile.subscription.status === SubscriptionStatus.Paused) {
      content.push(
        <p key="paused"
          className="font-bold"
          data-test="qr-code-paused"
        >
          Movie Club benefits (including 20% concession discount and member pricing on tickets) are unavailable while paused.
        </p>
      );
    }

    if (profile.subscription) {
      content.push(
        <p key="can-show-subscriptions"
          className="font-bold"
          data-test="qr-code-help-text"
        >
          Scan at the box office and concession stand to earn points and unlock rewards and use your Movie Club credits.
        </p>
      );

      if (profile.subscription.tier === 1) {
        content.push(
          <React.Fragment key="platinum-benefits">
            <p key="platinum-snacks"
              className="font-bold"
              data-test="qr-code-platinum-snacks"
            >
              Save on all snacks &amp; drinks as a Platinum member.
            </p>
            <p key="platinum-points"
              className="font-bold"
              data-test="qr-code-platinum-points"
            >
              Earn points &amp; unlock rewards.
            </p>
          </React.Fragment>
        );
      }
    } else {
      content.push(
        <p key="no-subscription"
          className="font-bold"
          data-test="qr-code-no-subscription"
        >
          Scan at the box office and concession stand to earn points and unlock rewards.
        </p>
      );
    }

    return <>{content}</>;
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>

      <DialogContent className={qrCodeModalStyles['modal-content']}>
        <DialogHeader>
          <DialogTitle className="hide" />
          <DialogDescription className={qrCodeModalStyles['modal-body']}>
            {profile.isMovieClubMember &&
              <div className={qrCodeModalStyles['user-info']}>
                <Lottie height={80}
                  options={lottieOptions}
                  style={{ margin: 0 }}
                  width={80}
                />

                <div className={qrCodeModalStyles['plan-details']}>
                  <h2 data-test="qr-code-modal-header">Movie Club Member
                    <span className={clsx('font-bold primary', qrCodeModalStyles['dashboard-status'])}
                      data-test="qr-code-modal-member-status"
                    >
                      {profile.subscription.statusMessage}
                    </span>
                  </h2>
                  <p  data-test="qr-code-modal-member-since">{(profile.created || new Date()).toLocaleDateString()}</p>
                </div>
              </div>
            }

            {displaySubscriptionStateText()}

            <div className={qrCodeModalStyles['qr-code-details']}>
              <Image alt="account qr code"
                height={296}
                src={qrCode}
                width={296}
              />

              <p className="text-xs"
                data-test="qr-code-modal-member-id"
              >{profile.memberId}
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
